import React from "react";
import { BookOpen, GraduationCap, Quote, Users, Rss } from "lucide-react";

const PHOTO_URL = process.env.REACT_APP_PHOTO_URL;

const credentials = [
  { icon: GraduationCap, text: "PhD in Quantum Computing" },
  { icon: BookOpen, text: "Author, Financial Modeling Using Quantum Computing (Packt, 2023)" },
  { icon: BookOpen, text: "Author, QML Unlocked (2025)" },
  { icon: Users, text: "25,000+ LinkedIn Followers" },
  { icon: Rss, text: "2,000+ Blog Subscribers" },
];

export default function FounderSection() {
  return (
    <section
      id="about"
      data-testid="founder-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section label */}
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#34D399]/80 mb-12 block">
          // The Founder
        </span>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Photo column */}
          <div className="md:col-span-4">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-b from-[#34D399]/20 to-transparent rounded-2xl blur-sm" />
              <img
                src={PHOTO_URL}
                alt="Javier Mancilla Montero"
                data-testid="founder-photo"
                className="relative w-full aspect-[3/4] object-cover object-top rounded-2xl founder-photo"
              />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="glass-card rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#34D399]" data-testid="stat-citations">76+</div>
                <div className="text-xs font-mono text-[#94A3B8] mt-1">Citations</div>
              </div>
              <div className="glass-card rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-[#34D399]" data-testid="stat-publications">11+</div>
                <div className="text-xs font-mono text-[#94A3B8] mt-1">Publications</div>
              </div>
            </div>
          </div>

          {/* Bio column */}
          <div className="md:col-span-8">
            <h2
              data-testid="founder-name"
              className="text-3xl md:text-5xl font-bold tracking-tight text-[#F0FDF4] mb-2"
            >
              Javier Mancilla Montero
            </h2>
            <p className="text-[#34D399] font-mono text-sm mb-8">
              PhD &mdash; Founder & Principal Advisor
            </p>

            <div className="space-y-6 text-[#94A3B8] text-base md:text-lg leading-relaxed">
              <p data-testid="founder-bio">
                With over 15 years at the forefront of computational science, 
                I specialize in bridging the gap between cutting-edge quantum algorithms 
                and real-world financial applications. My work focuses on translating 
                theoretical breakthroughs in quantum computing into 
                actionable strategies for portfolio optimization, risk modeling, and 
                credit assessment.
              </p>
              <p>
                As the author of{" "}
                <span className="text-[#F0FDF4] font-medium">
                  Financial Modeling Using Quantum Computing
                </span>{" "}
                (Packt, 2023) and{" "}
                <span className="text-[#F0FDF4] font-medium">QML Unlocked</span>{" "}
                (2025), I bring both deep academic rigor and practical experience to
                every engagement. A recognized LinkedIn Quantum Top Voice with a
                global following, I actively share insights on quantum computing
                applied to finance.
              </p>
            </div>

            {/* Credentials */}
            <div className="mt-8 space-y-3">
              {credentials.map((cred, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 text-[#94A3B8]"
                >
                  <cred.icon size={18} className="text-[#34D399] mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{cred.text}</span>
                </div>
              ))}
            </div>

            {/* Collaborators note */}
            <div className="mt-10 glass-card rounded-xl p-6 flex items-start gap-4">
              <Quote size={24} className="text-[#34D399]/50 flex-shrink-0 mt-1" />
              <p data-testid="collaborators-note" className="text-[#94A3B8] text-sm leading-relaxed">
                Cybersyn Dynamics is backed by a carefully curated network of 
                collaborators spanning quantum physics, quantum machine learning, and 
                quantitative finance&mdash;ensuring every project benefits from 
                multidisciplinary expertise and global perspectives.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
